// Vertical layout constants for the schedule timeline. A single-lane row keeps
// the original 44px height (ROW_PADDING*2 + BAR_HEIGHT); each extra lane adds
// LANE_PITCH so stacked bars never overlap.
export const BAR_HEIGHT = 26;
export const LANE_GAP = 4;
export const LANE_PITCH = BAR_HEIGHT + LANE_GAP; // 30
export const ROW_PADDING = 9;

export interface LaneBar {
  /** First visible day-of-month the bar occupies (1-based). */
  startDay: number;
  /** Number of day cells the bar spans. */
  spanDays: number;
}

/**
 * HW-701: greedy lane packing. Assigns each bar the lowest lane that is free at
 * its start day, so bars overlapping in days (e.g. an approved leave and a
 * pending request for the same person) land on separate lanes instead of one
 * silently hiding the other. Returns a lane index per input bar (input order
 * preserved) and the total lane count (min 1).
 */
export function assignBarLanes<T extends LaneBar>(
  bars: T[]
): { lanes: number[]; laneCount: number } {
  // Process in start-day order so the greedy earliest-free-lane choice is stable.
  const order = bars.map((_, i) => i).sort((a, b) => bars[a].startDay - bars[b].startDay);
  const laneEnds: number[] = []; // last occupied day-of-month per lane
  const lanes = new Array<number>(bars.length).fill(0);

  for (const i of order) {
    const start = bars[i].startDay;
    const end = start + bars[i].spanDays - 1;
    let lane = laneEnds.findIndex((endDay) => endDay < start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(end);
    } else {
      laneEnds[lane] = end;
    }
    lanes[i] = lane;
  }

  return { lanes, laneCount: Math.max(1, laneEnds.length) };
}

/** Total row height needed to fit `laneCount` stacked bars. */
export function rowHeightForLanes(laneCount: number): number {
  return ROW_PADDING * 2 + BAR_HEIGHT + (Math.max(1, laneCount) - 1) * LANE_PITCH;
}

/** Top offset (px, from the cell top) for a bar on the given lane. */
export function barTopForLane(lane: number): number {
  return ROW_PADDING + lane * LANE_PITCH;
}
